import { createTheme } from '@mui/material/styles';

// User-specified blue color palette
const blueColorPalette = {
  // Primary Blues
  'blue-600': '#2563eb', // main blue for gradients and primary elements
  'blue-700': '#1d4ed8', // darker blue for hover states
  'blue-500': '#3b82f6', // medium blue for focus states and accents
  
  // Indigo Colors (blue-purple blend)
  'indigo-600': '#4f46e5', // used in gradients with blue-600
  'indigo-700': '#4338ca', // darker indigo for hover states
  
  // Background Blues
  'blue-50': '#eff6ff', // very light blue for background gradient
  'blue-100': '#dbeafe', // light blue for feature icon backgrounds
  
  // Additional blue shades for completeness
  'blue-200': '#bfdbfe',
  'blue-300': '#93c5fd',
  'blue-400': '#60a5fa',
  'blue-800': '#1e40af',
  'blue-900': '#1e3a8a',
};

// Dynamic theme color generator - now using the specific blue palette
const generateColorPalette = (baseColor = blueColorPalette['blue-600']) => {
  // Return the predefined blue palette instead of generating one
  return {
    50: blueColorPalette['blue-50'],
    100: blueColorPalette['blue-100'],
    200: blueColorPalette['blue-200'],
    300: blueColorPalette['blue-300'],
    400: blueColorPalette['blue-400'],
    500: blueColorPalette['blue-500'],
    600: blueColorPalette['blue-600'], // Main brand color
    700: blueColorPalette['blue-700'],
    800: blueColorPalette['blue-800'],
    900: blueColorPalette['blue-900'],
  };
};

// Secondary color palette (neutral grays)
const secondaryColors = {
  50: '#f8fafc',
  100: '#f1f5f9',
  200: '#e2e8f0',
  300: '#cbd5e1',
  400: '#94a3b8',
  500: '#64748b',
  600: '#475569',
  700: '#334155',
  800: '#1e293b',
  900: '#0f172a',
};

// Create theme utility functions
const createThemeUtils = (primaryColors) => {
  // Gradient generator function
  const createGradient = (color1, color2, direction = '135deg') => 
    `linear-gradient(${direction}, ${color1} 0%, ${color2} 100%)`;

  // Theme-based shadow generator
  const createShadow = (color, opacity = 0.3, size = 'medium') => {
    const sizes = {
      small: '0px 2px 8px',
      medium: '0px 4px 12px',
      large: '0px 8px 25px',
      xlarge: '0px 12px 35px',
    };
    return `${sizes[size]} ${color}${Math.round(opacity * 255).toString(16).padStart(2, '0')}`;
  };

  // Standardized form component themes
  const formComponentThemes = {
    textField: {
      borderRadius: 12,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      transition: 'all 0.2s ease-in-out',
      hover: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        transform: 'translateY(-1px)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
      },
      focus: {
        backgroundColor: '#ffffff',
        borderWidth: '2px',
        transform: 'translateY(-1px)',
      }
    },
    select: {
      borderRadius: 12,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      transition: 'all 0.2s ease-in-out',
      hover: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
      },
      focus: {
        backgroundColor: '#ffffff',
        borderWidth: '2px',
      }
    },
    dialog: {
      borderRadius: 16,
      elevation: 8,
    },
    dataGrid: {
      borderRadius: 12,
      headerBackgroundColor: `${primaryColors[500]}14`, // 8% opacity
      borderColor: secondaryColors[200],
      hoverColor: secondaryColors[50],
      selectedColor: `${primaryColors[500]}14`, // 8% opacity
    }
  };

  // Standardized button themes for different contexts
  const buttonThemes = {
    auth: {
      borderRadius: 12,
      padding: [1.5, 3],
      fontSize: '1.1rem',
      boxShadow: 3,
      hoverBoxShadow: 4,
      transform: {
        hover: 'translateY(-2px)',
        active: 'translateY(0px)',
      },
    },
    action: {
      borderRadius: 12,
      padding: [1, 3],
      fontSize: '0.95rem',
      boxShadow: 1,
      hoverBoxShadow: 2,
      transform: {
        hover: 'translateY(-1px)',
        active: 'translateY(0px)',
      },
    },
    form: {
      borderRadius: 8,
      padding: [1, 2.5],
      fontSize: '0.9rem',
      minWidth: 100,
      boxShadow: 0,
      hoverBoxShadow: 1,
      transform: {
        hover: 'translateY(-1px)',
        active: 'translateY(0px)',
      },
    },
    icon: {
      borderRadius: 8,
      padding: [0.75, 1.5],
      fontSize: '0.875rem',
      minWidth: 'auto',
      boxShadow: 0,
      hoverBoxShadow: 1,
      transform: {
        hover: 'scale(1.05)',
        active: 'scale(0.95)',
      },
    },
  };

  // Create theme gradients - using the main gradient from blue-600 to indigo-600
  const gradients = {
    primary: createGradient(blueColorPalette['blue-600'], blueColorPalette['indigo-600']),
    primaryReverse: createGradient(blueColorPalette['indigo-600'], blueColorPalette['blue-600']),
    primaryDark: createGradient(blueColorPalette['blue-700'], blueColorPalette['indigo-700']),
    backgroundLight: createGradient(blueColorPalette['blue-50'], '#ffffff', '180deg'),
    success: createGradient('#10b981', '#059669'),
    successReverse: createGradient('#059669', '#10b981'),
    error: createGradient('#ef4444', '#dc2626'),
    errorReverse: createGradient('#dc2626', '#ef4444'),
    warning: createGradient('#f59e0b', '#d97706'),
    info: createGradient(blueColorPalette['blue-500'], blueColorPalette['blue-600']),
  };

  // Create theme shadows
  const shadows = {
    primary: {
      small: createShadow(primaryColors[500], 0.2, 'small'),
      medium: createShadow(primaryColors[500], 0.3, 'medium'),
      large: createShadow(primaryColors[500], 0.4, 'large'),
      xlarge: createShadow(primaryColors[500], 0.5, 'xlarge'),
    },
    success: {
      medium: createShadow('#10b981', 0.3, 'medium'),
      large: createShadow('#10b981', 0.4, 'large'),
    },
    error: {
      medium: createShadow('#ef4444', 0.3, 'medium'),
      large: createShadow('#ef4444', 0.4, 'large'),
    },
    warning: {
      medium: createShadow('#f59e0b', 0.3, 'medium'),
      large: createShadow('#f59e0b', 0.4, 'large'),
    },
  };

  return {
    createGradient,
    createShadow,
    formComponentThemes,
    buttonThemes,
    gradients,
    shadows,
  };
};

// Create theme factory function
const createEasyCheckTheme = (brandColor = blueColorPalette['blue-600']) => {
  const primaryColors = generateColorPalette(brandColor);
  const utils = createThemeUtils(primaryColors);

  return createTheme({
  palette: {
    mode: 'light',
    primary: {
        main: primaryColors[600], // blue-600
        light: primaryColors[500], // blue-500
        dark: primaryColors[700], // blue-700
      contrastText: '#ffffff',
    },
    secondary: {
      main: secondaryColors[500],
      light: secondaryColors[300],
      dark: secondaryColors[700],
      contrastText: '#ffffff',
    },
    background: {
        default: primaryColors[50], // blue-50
      paper: '#ffffff',
    },
    text: {
      primary: secondaryColors[700],
      secondary: secondaryColors[500],
    },
    error: {
      main: '#ef4444',
      light: '#fca5a5',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fbbf24',
      dark: '#d97706',
    },
    info: {
      main: primaryColors[500], // blue-500
      light: primaryColors[400],
      dark: primaryColors[600],
    },
    success: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
    },
    grey: secondaryColors,
  },
    
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      color: secondaryColors[800],
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: secondaryColors[800],
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
      color: secondaryColors[800],
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: secondaryColors[800],
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: secondaryColors[800],
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.4,
      color: secondaryColors[800],
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
      color: secondaryColors[700],
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
      color: secondaryColors[600],
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 0,
  },
  shadows: [
    'none',
      utils.createShadow(primaryColors[500], 0.1, 'small'),
      utils.createShadow(primaryColors[500], 0.1, 'small'),
      utils.createShadow(primaryColors[500], 0.15, 'medium'),
      utils.createShadow(primaryColors[500], 0.2, 'large'),
      utils.createShadow(primaryColors[500], 0.25, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[500], 0.3, 'xlarge'),
      utils.createShadow(primaryColors[600], 0.35, 'xlarge'),
      utils.createShadow(primaryColors[600], 0.35, 'xlarge'),
      utils.createShadow(primaryColors[600], 0.35, 'xlarge'),
  ],
  components: {
      // Button overrides with dynamic theme colors
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          padding: '10px 24px',
          fontSize: '0.95rem',
          fontWeight: 600,
          boxShadow: 'none',
          textTransform: 'none',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
              boxShadow: utils.createShadow(primaryColors[500], 0.3, 'medium'),
            transform: 'translateY(-1px)',
          },
        },
        contained: {
            background: utils.gradients.primary, // blue-600 to indigo-600 gradient
          '&:hover': {
              background: utils.gradients.primaryDark, // blue-700 to indigo-700 gradient
            },
        },
        outlined: {
            borderColor: primaryColors[200],
            color: primaryColors[600],
          '&:hover': {
              borderColor: primaryColors[500],
              backgroundColor: primaryColors[50],
            },
          },
        },
      },
      
      // Enhanced TextField overrides
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
              borderRadius: utils.formComponentThemes.textField.borderRadius,
              backgroundColor: utils.formComponentThemes.textField.backgroundColor,
              transition: utils.formComponentThemes.textField.transition,
            '&:hover': {
                backgroundColor: utils.formComponentThemes.textField.hover.backgroundColor,
                transform: utils.formComponentThemes.textField.hover.transform,
                boxShadow: utils.formComponentThemes.textField.hover.boxShadow,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColors[300],
                },
              },
              '&.Mui-focused': {
                backgroundColor: utils.formComponentThemes.textField.focus.backgroundColor,
                transform: utils.formComponentThemes.textField.focus.transform,
                boxShadow: utils.createShadow(primaryColors[500], 0.25, 'small'),
              '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColors[500],
                  borderWidth: utils.formComponentThemes.textField.focus.borderWidth,
                },
              },
              '&.Mui-error': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ef4444',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ef4444',
                  boxShadow: utils.createShadow('#ef4444', 0.25, 'small'),
                },
              },
            },
            '& .MuiInputLabel-root': {
              color: secondaryColors[500],
              fontWeight: 500,
            '&.Mui-focused': {
                color: primaryColors[500],
                fontWeight: 600,
              },
              '&.Mui-error': {
                color: '#ef4444',
              },
            },
            '& .MuiFormHelperText-root': {
              fontSize: '0.75rem',
              marginLeft: 4,
              '&.Mui-error': {
                color: '#ef4444',
              },
            },
          },
        },
      },

      // Enhanced FormControl and Select overrides
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: utils.formComponentThemes.select.borderRadius,
              backgroundColor: utils.formComponentThemes.select.backgroundColor,
              transition: utils.formComponentThemes.select.transition,
              '&:hover': {
                backgroundColor: utils.formComponentThemes.select.hover.backgroundColor,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColors[300],
                },
              },
              '&.Mui-focused': {
                backgroundColor: utils.formComponentThemes.select.focus.backgroundColor,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: primaryColors[500],
                  borderWidth: utils.formComponentThemes.select.focus.borderWidth,
                },
              },
              '&.Mui-error': {
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ef4444',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ef4444',
              },
            },
          },
          '& .MuiInputLabel-root': {
            color: secondaryColors[500],
              fontWeight: 500,
            '&.Mui-focused': {
                color: primaryColors[500],
                fontWeight: 600,
              },
              '&.Mui-error': {
                color: '#ef4444',
              },
            },
          },
        },
      },

      // Select component overrides
      MuiSelect: {
        styleOverrides: {
          root: {
            '&.MuiOutlinedInput-root': {
              borderRadius: utils.formComponentThemes.select.borderRadius,
              backgroundColor: utils.formComponentThemes.select.backgroundColor,
              transition: utils.formComponentThemes.select.transition,
            },
          },
          icon: {
            color: primaryColors[400],
            '&.Mui-disabled': {
              color: secondaryColors[300],
            },
          },
        },
      },

      // Menu and MenuItem overrides for Select dropdowns
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            boxShadow: utils.createShadow(primaryColors[500], 0.15, 'large'),
            border: `1px solid ${primaryColors[100]}`,
            marginTop: 4,
          },
        },
      },

      MuiMenuItem: {
        styleOverrides: {
          root: {
            padding: '10px 16px',
            fontSize: '0.9rem',
            transition: 'all 0.15s ease-in-out',
            '&:hover': {
              backgroundColor: primaryColors[50],
              color: primaryColors[700],
            },
            '&.Mui-selected': {
              backgroundColor: primaryColors[100],
              color: primaryColors[700],
              fontWeight: 600,
              '&:hover': {
                backgroundColor: primaryColors[200],
              },
            },
          },
        },
      },

      // Enhanced Dialog overrides
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: utils.formComponentThemes.dialog.borderRadius,
            boxShadow: utils.createShadow(primaryColors[500], 0.2, 'xlarge'),
            border: `1px solid ${primaryColors[100]}`,
          },
        },
      },

      MuiDialogTitle: {
        styleOverrides: {
          root: {
            backgroundColor: primaryColors[50],
            borderBottom: `1px solid ${primaryColors[100]}`,
            color: primaryColors[700],
            fontWeight: 600,
            fontSize: '1.25rem',
          },
        },
      },

      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '24px',
            backgroundColor: '#fefefe',
          },
        },
      },

      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            backgroundColor: secondaryColors[25] || '#fafbfc',
            borderTop: `1px solid ${secondaryColors[100]}`,
            gap: '12px',
          },
        },
      },

      // DataGrid overrides for consistency
      MuiDataGrid: {
        styleOverrides: {
          root: {
            border: `1px solid ${utils.formComponentThemes.dataGrid.borderColor}`,
            borderRadius: utils.formComponentThemes.dataGrid.borderRadius,
            backgroundColor: 'white',
            '& .MuiDataGrid-main': {
              borderRadius: utils.formComponentThemes.dataGrid.borderRadius,
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: utils.formComponentThemes.dataGrid.headerBackgroundColor,
              borderBottom: `2px solid ${primaryColors[100]}`,
              '& .MuiDataGrid-columnHeader': {
                fontWeight: 600,
                fontSize: '0.875rem',
                color: primaryColors[600],
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 600,
              },
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                backgroundColor: utils.formComponentThemes.dataGrid.hoverColor,
              },
              '&.Mui-selected': {
                backgroundColor: utils.formComponentThemes.dataGrid.selectedColor,
                '&:hover': {
                  backgroundColor: `${primaryColors[500]}1f`, // 12% opacity
                },
              },
            },
            '& .MuiDataGrid-cell': {
              borderBottom: `1px solid ${secondaryColors[100]}`,
              fontSize: '0.875rem',
              '&:focus': {
                outline: `2px solid ${primaryColors[300]}`,
                outlineOffset: '-2px',
              },
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${utils.formComponentThemes.dataGrid.borderColor}`,
              backgroundColor: secondaryColors[25] || '#fafbfc',
            },
            '& .MuiDataGrid-toolbarContainer': {
              padding: '12px 16px',
              borderBottom: `1px solid ${utils.formComponentThemes.dataGrid.borderColor}`,
              backgroundColor: secondaryColors[25] || '#fafbfc',
          },
        },
      },
    },

    // Card overrides
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundImage: 'none',
            boxShadow: utils.createShadow(primaryColors[500], 0.08, 'small'),
            border: `1px solid ${secondaryColors[100]}`,
          },
        },
      },

    // Paper overrides
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundImage: 'none',
        },
        elevation1: {
            boxShadow: utils.createShadow(primaryColors[500], 0.1, 'small'),
          },
        },
      },

    // AppBar overrides
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
        },
      },
    },

    // Drawer overrides
    MuiDrawer: {
      styleOverrides: {
        paper: {
            borderRight: `1px solid ${primaryColors[100]}`,
            background: utils.createGradient(primaryColors[50], '#ffffff', '180deg'),
          },
        },
      },

    // List Item Button overrides
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          margin: '4px 8px',
          '&:hover': {
              backgroundColor: primaryColors[100],
            transform: 'translateX(4px)',
          },
          '&.Mui-selected': {
              backgroundColor: primaryColors[100],
              color: primaryColors[600],
            '&:hover': {
                backgroundColor: primaryColors[200],
            },
            '& .MuiListItemIcon-root': {
                color: primaryColors[500],
              },
            },
          },
        },
      },

    // Chip overrides
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 0,
            fontWeight: 500,
          },
          colorPrimary: {
            backgroundColor: primaryColors[100],
            color: primaryColors[700],
            '&:hover': {
              backgroundColor: primaryColors[200],
            },
          },
        },
      },

    // Alert overrides
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
            border: '1px solid',
            fontWeight: 500,
        },
        standardError: {
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderColor: 'rgba(239, 68, 68, 0.2)',
            color: '#dc2626',
        },
        standardWarning: {
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
            borderColor: 'rgba(245, 158, 11, 0.2)',
            color: '#d97706',
        },
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
            borderColor: 'rgba(59, 130, 246, 0.2)',
            color: '#2563eb',
        },
        standardSuccess: {
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
            borderColor: 'rgba(16, 185, 129, 0.2)',
            color: '#059669',
          },
        },
      },

    // Loading Button overrides
    MuiLoadingButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },

    // Tab overrides
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '0.95rem',
          color: secondaryColors[500],
          '&.Mui-selected': {
              color: primaryColors[500],
            },
          },
        },
      },

    // Tabs overrides
    MuiTabs: {
      styleOverrides: {
        indicator: {
            backgroundColor: primaryColors[500],
          height: 3,
          borderRadius: 2,
        },
      },
    },

      // Switch overrides
      MuiSwitch: {
        styleOverrides: {
          root: {
            '& .MuiSwitch-switchBase.Mui-checked': {
              color: primaryColors[500],
              '& + .MuiSwitch-track': {
                backgroundColor: primaryColors[300],
              },
            },
          },
          track: {
            backgroundColor: secondaryColors[300],
          },
        },
      },

      // Checkbox overrides
      MuiCheckbox: {
      styleOverrides: {
          root: {
            color: secondaryColors[400],
            '&.Mui-checked': {
              color: primaryColors[500],
            },
            '&:hover': {
              backgroundColor: primaryColors[50],
            },
          },
        },
      },

      // Radio overrides
      MuiRadio: {
        styleOverrides: {
          root: {
            color: secondaryColors[400],
            '&.Mui-checked': {
              color: primaryColors[500],
            },
            '&:hover': {
              backgroundColor: primaryColors[50],
            },
        },
      },
    },

      // Autocomplete overrides
      MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
            boxShadow: utils.createShadow(primaryColors[500], 0.15, 'large'),
            border: `1px solid ${primaryColors[100]}`,
          },
          option: {
            padding: '10px 16px',
            '&:hover': {
              backgroundColor: primaryColors[50],
            },
            '&.Mui-focused': {
              backgroundColor: primaryColors[100],
            },
          },
        },
      },
    },
  });
};

// Create default theme with brand blue
const theme = createEasyCheckTheme(blueColorPalette['blue-600']);

// Export theme utilities for consistent usage
export const themeUtils = {
  colors: {
    primary: generateColorPalette(blueColorPalette['blue-600']),
    secondary: secondaryColors,
    blue: blueColorPalette,
  },
  ...createThemeUtils(generateColorPalette(blueColorPalette['blue-600'])),
};

// Export theme factory for custom brand colors
export const createCustomTheme = createEasyCheckTheme;

export default theme; 